// src/components/EventSponsorsBlock.js
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
  Linking,
} from 'react-native';

export function openSafeHttpsUrl(url) {
  if (!url || typeof url !== 'string' || !/^https:\/\/[^\s]+$/i.test(url.trim())) return;
  const clean = url.trim();
  if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.open === 'function') {
    window.open(clean, '_blank', 'noopener,noreferrer');
  } else if (Linking?.openURL) {
    Linking.openURL(clean).catch(() => {});
  }
}

export function sponsorLogoUri(s) {
  if (!s) return null;
  if (typeof s.logoUrl === 'string' && s.logoUrl.trim()) return s.logoUrl.trim();
  if (typeof s.logoAssetId === 'string' && /^[a-f0-9]{32}$/.test(s.logoAssetId)) {
    return `/sponsor-logo/${s.logoAssetId}`;
  }
  return null;
}

export function EventSponsorsBlock({ ev }) {
  if (!ev || ev.branded !== true) return null;

  const organizerName = ev.organizer?.name ? String(ev.organizer.name).trim() : '';
  const organizerUrl = ev.organizer?.url ? String(ev.organizer.url).trim() : '';
  const disclaimer = ev.publisherDisclaimer ? String(ev.publisherDisclaimer).trim() : '';
  const rawSponsors = Array.isArray(ev.sponsors) ? ev.sponsors.filter((s) => s && s.name) : [];

  const presenting = rawSponsors.filter((s) => s.level === 'presenting');
  const featured = rawSponsors.filter((s) => s.level === 'featured');
  const community = rawSponsors.filter((s) => s.level === 'community');
  const prizeItems = rawSponsors.filter((s) => s.prizeNote && String(s.prizeNote).trim());

  const hasAnything = Boolean(
    organizerName
    || presenting.length > 0
    || featured.length > 0
    || community.length > 0
    || prizeItems.length > 0
    || disclaimer
  );
  if (!hasAnything) return null;

  return (
    <View style={sponsorBlockStyles.card}>
      {Boolean(organizerName) && (
        <View style={sponsorBlockStyles.organizerRow}>
          {organizerUrl ? (
            <TouchableOpacity onPress={() => openSafeHttpsUrl(organizerUrl)} activeOpacity={0.8}>
              <Text style={[sponsorBlockStyles.organizerText, sponsorBlockStyles.linkText]}>
                {`Organized by ${organizerName}`}
              </Text>
            </TouchableOpacity>
          ) : (
            <Text style={sponsorBlockStyles.organizerText}>{`Organized by ${organizerName}`}</Text>
          )}
        </View>
      )}

      {presenting.length > 0 && (
        <View style={sponsorBlockStyles.group}>
          <Text style={sponsorBlockStyles.groupLabel}>Presented by</Text>
          <View style={sponsorBlockStyles.presentingRow}>
            {presenting.map((s, idx) => {
              const logoUri = sponsorLogoUri(s);
              const content = (
                <View style={sponsorBlockStyles.presentingCard}>
                  {logoUri && (
                    <Image source={{ uri: logoUri }} style={sponsorBlockStyles.presentingLogo} resizeMode="contain" />
                  )}
                  <Text style={sponsorBlockStyles.presentingName}>{s.name}</Text>
                </View>
              );
              return s.url ? (
                <TouchableOpacity key={s.sponsorshipId || `pres_${idx}`} onPress={() => openSafeHttpsUrl(s.url)} activeOpacity={0.85}>
                  {content}
                </TouchableOpacity>
              ) : (
                <View key={s.sponsorshipId || `pres_${idx}`}>{content}</View>
              );
            })}
          </View>
        </View>
      )}

      {featured.length > 0 && (
        <View style={sponsorBlockStyles.group}>
          <Text style={sponsorBlockStyles.groupLabel}>Featured sponsors</Text>
          <View style={sponsorBlockStyles.logoRow}>
            {featured.map((s, idx) => {
              const logoUri = sponsorLogoUri(s);
              const content = (
                <View style={sponsorBlockStyles.featuredItem}>
                  {logoUri && (
                    <Image source={{ uri: logoUri }} style={sponsorBlockStyles.featuredLogo} resizeMode="contain" />
                  )}
                  <Text style={sponsorBlockStyles.featuredName}>{s.name}</Text>
                </View>
              );
              return s.url ? (
                <TouchableOpacity key={s.sponsorshipId || `feat_${idx}`} onPress={() => openSafeHttpsUrl(s.url)} activeOpacity={0.85}>
                  {content}
                </TouchableOpacity>
              ) : (
                <View key={s.sponsorshipId || `feat_${idx}`}>{content}</View>
              );
            })}
          </View>
        </View>
      )}

      {community.length > 0 && (
        <View style={sponsorBlockStyles.group}>
          <Text style={sponsorBlockStyles.groupLabel}>Community sponsors</Text>
          <View style={sponsorBlockStyles.communityRow}>
            {community.map((s, idx) => {
              const logoUri = sponsorLogoUri(s);
              const pill = (
                <View style={sponsorBlockStyles.communityChip}>
                  {logoUri && (
                    <Image source={{ uri: logoUri }} style={sponsorBlockStyles.communityLogo} resizeMode="contain" />
                  )}
                  <Text style={sponsorBlockStyles.communityName}>{s.name}</Text>
                </View>
              );
              return s.url ? (
                <TouchableOpacity key={s.sponsorshipId || `comm_${idx}`} onPress={() => openSafeHttpsUrl(s.url)} activeOpacity={0.85}>
                  {pill}
                </TouchableOpacity>
              ) : (
                <View key={s.sponsorshipId || `comm_${idx}`}>{pill}</View>
              );
            })}
          </View>
        </View>
      )}

      {prizeItems.length > 0 && (
        <View style={sponsorBlockStyles.group}>
          <Text style={sponsorBlockStyles.groupLabel}>Additional prizes</Text>
          <View style={sponsorBlockStyles.prizeList}>
            {prizeItems.map((s, idx) => (
              <Text key={s.sponsorshipId || `prize_${idx}`} style={sponsorBlockStyles.prizeText}>
                {`Prize from ${s.name}: ${String(s.prizeNote).trim()}`}
              </Text>
            ))}
          </View>
        </View>
      )}

      {Boolean(disclaimer) && (
        <Text style={sponsorBlockStyles.disclaimerText}>{disclaimer}</Text>
      )}
    </View>
  );
}

const sponsorBlockStyles = StyleSheet.create({
  card: {
    backgroundColor: '#121526',
    borderWidth: 1,
    borderColor: '#252a48',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    gap: 12,
  },
  organizerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  organizerText: {
    color: '#c9d1d9',
    fontSize: 13,
    fontWeight: '700',
  },
  linkText: {
    color: '#FCD000',
    textDecorationLine: 'underline',
  },
  group: {
    gap: 6,
  },
  groupLabel: {
    color: '#9aa0b4',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  presentingRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
  },
  presentingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#191d34',
    borderWidth: 1,
    borderColor: '#FCD000',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  presentingLogo: {
    width: 120,
    height: 44,
  },
  presentingName: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '900',
  },
  logoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
  },
  featuredItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#181c30',
    borderWidth: 1,
    borderColor: '#2a2f4a',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  featuredLogo: {
    width: 84,
    height: 32,
  },
  featuredName: {
    color: '#E7ECFF',
    fontSize: 14,
    fontWeight: '800',
  },
  communityRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  communityChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#16192b',
    borderWidth: 1,
    borderColor: '#232842',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  communityLogo: {
    width: 48,
    height: 20,
  },
  communityName: {
    color: '#c9d1d9',
    fontSize: 12,
    fontWeight: '700',
  },
  prizeList: {
    gap: 4,
  },
  prizeText: {
    color: '#E7ECFF',
    fontSize: 13,
    fontWeight: '600',
  },
  disclaimerText: {
    color: '#8b92a8',
    fontSize: 11,
    marginTop: 4,
  },
});

export default EventSponsorsBlock;

